// D3-7 — ONE TRANSCRIPT, FULL, BOTH SIDES, READ-ONLY. PATH B.
//
// ONE COMPONENT, TWO DOORS. The usage page opens a transcript from a member's conversation list; the
// review queue opens one from a flagged turn. Both land here. A transcript renderer written twice
// would be two answer keys for the most sensitive surface in the dashboard — one of them would get a
// redaction the other did not, and nobody would notice which.
//
// THE HIGHLIGHT IS THE REVIEW QUEUE'S WHOLE VALUE. D3-9 says a flag "links straight into the full
// transcript WITH THE FLAGGED TURN HIGHLIGHTED", so the turn is scrolled to and marked. Where the
// server could not identify the turn (the gate log carries no message id — see server/db/usage.js)
// the transcript still opens and simply says so, rather than silently highlighting the wrong line.
//
// R79 — **THE REASON FORM WENT AWAY BEFORE THE ANSWER CAME BACK.** `open()` set `asked` first and
// unconditionally, so a refused, dropped or locked-out open left her with a note, no transcript, and
// **no button to try again** — the reason she had typed still in state with nothing to submit it
// with. The only way back was closing the whole transcript. R61's ruling, on the surface where the
// act she is being asked to justify is reading somebody's private conversation.
//
// And the 403 discrimination inline below was the THIRD hand-typed copy of a law this house wrote
// down in a comment (R47: the count is the finding, and the copies had already drifted — this one
// falls back to "That could not be opened." and the usage page's falls back to ""). It is
// `src/lib/support-read.ts` now.
//
// R88 — **AND THE EXPORT WAS A PLAIN `<a href>`.** That route refuses four ways in JSON, so a
// refused export did not print a note, it NAVIGATED the dashboard to a raw JSON body — taking the
// transcript she had just justified opening and the reason she typed to open it. The likeliest
// refusal is the worst one: the support ticket is time-limited and READING is the slow act, so the
// locked 403 is arrived at by doing exactly what the feature is for. It reads its answer now, on the
// READ's own law, and the three-way sentence this file hand-typed once is `supportInnerNote`.
import { useEffect, useRef, useState } from "react";
import { REASON_NOTE, EXPORT_NOTE, NO_BULK_NOTE, EXPORT_FAILED, EXPORT_BUSY } from "../usagePage.js";
import { loadSupportData, supportInnerNote, saveSupportPdf } from "../lib/support-read";
import { saveBlobAs } from "../lib/downloads";
import { useRevealHere } from "./useRevealHere";

type Msg = { id: string; role: string; content: string; at: string };
type Data = {
  member: { id: string; email: string; name: string };
  transcript: { conversationId: string; title: string; messages: Msg[] };
  readOnlyNote: string;
  ledgerNote: string;
};

const stamp = (iso: string) => (iso ? new Date(iso).toLocaleString() : "");

export function OwnerTranscript({ memberId, conversationId, highlightTurnId = "", onClose, onDenied }: {
  memberId: string;
  conversationId: string;
  highlightTurnId?: string;
  onClose: () => void;
  onDenied: () => void;
}) {
  const [data, setData] = useState<Data | null>(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [asked, setAsked] = useState(false);
  // R88: the EXPORT's own two facts, kept apart from the OPEN's (R66's collision, avoided rather
  // than repaired). Opening a conversation and taking a copy of it out of the building are two acts
  // — the server's own ledger says so — and one state doing both jobs would print a failed export
  // above a transcript that opened perfectly well.
  const [exportSaid, setExportSaid] = useState("");
  const [exporting, setExporting] = useState(false);
  const marked = useRef<HTMLLIElement | null>(null);
  // Same fix, same reason: the transcript replaced a longer view and landed at top=-194.
  const here = useRevealHere<HTMLDivElement>([conversationId]);

  // THE REASON IS ASKED FOR BEFORE THE OPEN, AND IT IS OPTIONAL. Her revision to Path B made it
  // optional on purpose; asking after the fact would make it decoration, and requiring it would be
  // the Path A break-glass she explicitly replaced.
  async function open() {
    setNote("");
    const q = reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
    const answer = await loadSupportData<Data>(
      `/api/owner/support/member/${encodeURIComponent(memberId)}/transcript/${encodeURIComponent(conversationId || "drawer")}${q}`);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // **A 200 CARRYING NO TRANSCRIPT IS A REFUSAL** (R53's presence guard). Without it this view
    // renders `data.transcript.title` off a partial payload and takes the dashboard down with it —
    // the one failure on these two surfaces that is a blank page rather than a wrong sentence.
    if (answer.ok && answer.data?.transcript?.messages) {
      setData(answer.data);
      // **HELD BY POSITION (R52/R61): THE REASON FORM CLOSES ONLY ON AN OPEN THAT LANDED**, and her
      // typed reason survives a refusal — retyping a justification she just wrote is the one thing a
      // failed open must never cost her.
      setAsked(true);
      return;
    }
    // A LOCKED 403 SAYS SO HERE, WHERE `OwnerSupportView` STAYS SILENT ABOUT ITS OWN — and the
    // difference is argued rather than inconsistent: there the locked answer triggers an ACT (the
    // door is re-read and the passcode form comes back), and this component is mounted INSIDE that
    // already-open door with nothing of its own to re-read. So the sentence is all there is.
    // R88: that three-way expression is `supportInnerNote` now, in the law — it was about to be
    // typed a second time in this same file for the export (R47: the count is the finding).
    setNote(answer.ok ? "That conversation could not be opened."
      : supportInnerNote(answer, "That could not be opened."));
  }

  // R88 — **THE EXPORT READS ITS ANSWER.** It was an `<a href>`, so every refusal this route can
  // give navigated the dashboard away to raw JSON. The transcript, the reason and her place all
  // stay exactly where they are now, and what she gets instead is the sentence — the SERVER'S own
  // words where it sent any (R72), which on the likeliest refusal is the one telling her the
  // passcode door has closed behind her.
  async function exportPdf() {
    setExportSaid("");
    setExporting(true);
    const answer = await saveSupportPdf(exportHref, (res) => saveBlobAs(res));
    setExporting(false);
    // A `denied` 403 is still an ACT and not a sentence: the whole dashboard is closed.
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setExportSaid(supportInnerNote(answer, EXPORT_FAILED));
  }

  useEffect(() => {
    if (!marked.current) return;
    marked.current.scrollIntoView({ block: "center" });
  }, [data]);

  const exportHref = `/api/owner/support/member/${encodeURIComponent(memberId)}/transcript/${encodeURIComponent(conversationId || "drawer")}/pdf${reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ""}`;

  return (
    <div className="owner-transcript" data-transcript={conversationId || "drawer"} ref={here}>
      <div className="owner-detail__head">
        <h3 className="owner-detail__sub">{data ? data.transcript.title : "Conversation"}</h3>
        <button type="button" className="owner-link" onClick={onClose}>Close</button>
      </div>

      {!asked ? (
        <div className="owner-support__form">
          <label className="owner-grant-form__field owner-grant-form__field--wide">
            <span>{REASON_NOTE}</span>
            <input className="owner-search" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" aria-label="Why you opened it" />
          </label>
          <button type="button" className="button" onClick={() => { void open(); }}>Open the conversation</button>
        </div>
      ) : null}

      {note ? <p className="owner-page__note owner-fresh--failed" role="status">{note}</p> : null}

      {data ? (
        <>
          <p className="owner-page__note owner-support__readonly">{data.readOnlyNote}</p>
          <p className="owner-page__note">{data.ledgerNote}</p>
          {highlightTurnId && !data.transcript.messages.some((m) => m.id === highlightTurnId) ? (
            <p className="owner-page__note owner-support__walls">
              The flagged turn could not be matched to a message in this conversation, so nothing is highlighted.
              The whole conversation is here.
            </p>
          ) : null}

          <ol className="owner-transcript__list">
            {data.transcript.messages.map((m) => {
              const flagged = Boolean(highlightTurnId) && m.id === highlightTurnId;
              return (
                <li key={m.id} ref={flagged ? marked : null} data-turn={m.id}
                  className={`owner-turn owner-turn--${m.role === "assistant" ? "lala" : "member"}${flagged ? " is-flagged" : ""}`}>
                  <p className="owner-turn__who">
                    {m.role === "assistant" ? "Lala" : (data.member.name || data.member.email || "Member")}
                    <span className="owner-turn__at">{stamp(m.at)}</span>
                    {flagged ? <span className="owner-turn__flag">flagged turn</span> : null}
                  </p>
                  <p className="owner-turn__body">{m.content}</p>
                </li>
              );
            })}
          </ol>
          {!data.transcript.messages.length ? <p className="owner-page__note">This conversation has no turns recorded.</p> : null}

          {/* D3-7 — the export. ONE conversation, ledgered. The absence of a bulk control is said out
              loud, because a capability nobody built is invisible and a capability deliberately
              refused should not be. */}
          <div className="owner-transcript__export">
            {/* R88 — a BUTTON, not an anchor. The anchor could not read a refusal, and it could not
                say it was working either: a PDF this size takes a moment, and a control that sits
                there doing nothing reads as broken and gets tapped again (the ledger records the
                second one). The sentence stands IMMEDIATELY under the control it is about (R35's
                capture, R53's position) — the open's own note is at the top of this view and would
                be a failed export printed above a transcript that opened perfectly well. */}
            <button type="button" className="button button--ghost" disabled={exporting} onClick={() => { void exportPdf(); }}>
              {exporting ? EXPORT_BUSY : "Export this conversation as PDF"}
            </button>
            {exportSaid ? <p className="owner-page__note owner-fresh--failed" role="status">{exportSaid}</p> : null}
            <p className="owner-page__note">{EXPORT_NOTE}</p>
            <p className="owner-page__note">{NO_BULK_NOTE}</p>
          </div>
        </>
      ) : null}
    </div>
  );
}
